/**
 * ============================================================
 * MORNING FIX COFFEE
 * BUILD #7 — ORCHESTRATOR
 * VERSION 1.0.2
 * ============================================================
 *
 * RESPONSIBILITY
 * ------------------------------------------------------------
 * Build #7 is the orchestration layer for the Coffee Taste
 * Profile experience.
 *
 * Build #7:
 *   • Receives a completed coffee profile
 *   • Calls Build #2 for the landing-page result
 *   • Normalizes Build #2's result for Build #6
 *   • Starts Build #6
 *   • Supports update / restart / destroy lifecycle operations
 *   • Validates required dependencies
 *
 * Build #7 DOES NOT:
 *   • Score answers
 *   • Calculate profile dimensions
 *   • Determine roast results
 *   • Define landing pages
 *   • Resolve Shopify products
 *   • Add products to the Shopify cart
 *   • Manage subscriptions
 *   • Render the customer-facing UI
 *
 * ============================================================
 *
 * PIPELINE
 *
 *   BUILD #1
 *   Coffee Taste Profile Engine
 *          ↓
 *   BUILD #2
 *   Landing Page Router
 *          ↓
 *   BUILD #3
 *   Product Resolution
 *          ↓
 *   BUILD #4
 *   Shopify Cart
 *          ↓
 *   BUILD #5
 *   Subscription Experience
 *          ↓
 *   BUILD #6
 *   Customer-Facing UI
 *          ↑
 *   BUILD #7
 *   Orchestrator
 *
 * ============================================================
 */

(function (window) {

  'use strict';


  /* ==========================================================
     1. VERSION
     ========================================================== */

  const VERSION = '1.0.2';

  const BUILD = 7;


  /* ==========================================================
     2. STATE
     ========================================================== */

  const state = {

    initialized: false,

    profile: null,

    landingPage: null,

    container: null

  };


  /* ==========================================================
     3. BUILD #2 DEPENDENCY
     ========================================================== */

  function getLandingPages() {

    if (!window.CoffeeLandingPages) {

      throw new Error(
        'Build #7 requires Build #2: CoffeeLandingPages.'
      );

    }


    if (
      typeof window.CoffeeLandingPages.buildResult !==
      'function'
    ) {

      throw new Error(
        'Build #2 buildResult() is unavailable.'
      );

    }


    return window.CoffeeLandingPages;

  }


  /* ==========================================================
     3B. BUILD #3 DEPENDENCY
     ==========================================================

     Build #3 resolves Build #2's logical product keys into
     real Shopify variant/availability data. Without this
     step, every purchase option Build #6 receives would be
     missing variantId entirely, and every cart operation
     would fail regardless of whether Shopify products have
     actually been configured.

     ========================================================== */

  function getShopifyProducts() {

    if (!window.CoffeeShopifyProducts) {

      throw new Error(
        'Build #7 requires Build #3: CoffeeShopifyProducts.'
      );

    }


    if (
      typeof window.CoffeeShopifyProducts.resolveLandingPage !==
      'function'
    ) {

      throw new Error(
        'Build #3 resolveLandingPage() is unavailable.'
      );

    }


    return window.CoffeeShopifyProducts;

  }


  /* ==========================================================
     4. BUILD #6 DEPENDENCY
     ========================================================== */

  function getProfileUI() {

    if (!window.CoffeeTasteProfileUI) {

      throw new Error(
        'Build #7 requires Build #6: CoffeeTasteProfileUI.'
      );

    }


    if (
      typeof window.CoffeeTasteProfileUI.init !==
      'function'
    ) {

      throw new Error(
        'Build #6 init() is unavailable.'
      );

    }


    return window.CoffeeTasteProfileUI;

  }


  /* ==========================================================
     5. PROFILE VALIDATION
     ========================================================== */

  function validateProfile(profile) {

    if (!profile) {

      throw new Error(
        'Build #7 requires a completed coffee profile.'
      );

    }


    if (!profile.roastResult) {

      throw new Error(
        'Build #7 requires a coffee profile with a roast result.'
      );

    }


    if (!profile.roastResult.id) {

      throw new Error(
        'Build #7 coffee profile is missing roastResult.id.'
      );

    }


    return true;

  }


  /* ==========================================================
     6. CONTAINER RESOLUTION
     ========================================================== */

  function resolveContainer(container) {

    if (
      container &&
      typeof container === 'string'
    ) {

      container =
        document.querySelector(container);

    }


    if (
      container &&
      container.nodeType
    ) {

      return container;

    }


    const defaultContainer =
      document.getElementById(
        'coffee-taste-profile'
      );


    if (defaultContainer) {

      return defaultContainer;

    }


    throw new Error(
      'Build #7 could not find the Coffee Taste Profile container.'
    );

  }


  /* ==========================================================
     7. BUILD #2 → BUILD #6 ADAPTER
     ==========================================================
     *
     * Build #2 returns the orchestration result:
     *
     * {
     *   landingPage,
     *   landingPageName,
     *   landingPageType,
     *   purchaseOptions
     * }
     *
     * Build #6 expects a landing-page object containing
     * the information required by its rendering layer.
     *
     * This adapter deliberately keeps that translation here
     * instead of modifying either Build #2 or Build #6.
     * ========================================================== */

  function buildLandingPage(profile) {

    const landingPages =
      getLandingPages();


    const result =
      landingPages.buildResult(
        profile
      );


    if (!result) {

      throw new Error(
        'Build #2 returned no landing-page result.'
      );

    }


    if (!result.landingPage) {

      throw new Error(
        'Build #2 returned no landing-page identifier.'
      );

    }


    if (!result.landingPageType) {

      throw new Error(
        'Build #2 returned no landing-page type.'
      );

    }


    if (
      !Array.isArray(
        result.purchaseOptions
      )
    ) {

      throw new Error(
        'Build #2 returned invalid purchase options.'
      );

    }


    /*
     * BUILD #7 v1.0.3 FIX
     *
     * Build #2's purchaseOptions are logical only (key/name/type),
     * with no Shopify variantId or availability attached. Without
     * resolving through Build #3 here, Build #6 would receive
     * purchase options that can never be added to cart, regardless
     * of whether real Shopify products have been configured.
     */

    const shopifyProducts =
      getShopifyProducts();


    const resolvedResult =
      shopifyProducts.resolveLandingPage(
        result
      );


    if (
      !resolvedResult ||
      !Array.isArray(
        resolvedResult.purchaseOptions
      )
    ) {

      throw new Error(
        'Build #3 returned invalid resolved purchase options.'
      );

    }


    return {

      id:
        result.landingPage,

      resultId:
        result.resultId ||
        profile.roastResult.id,

      name:
        result.landingPageName,

      resultName:
        result.resultName ||
        profile.roastResult.name,

      type:
        result.landingPageType,

      landingPage:
        result.landingPage,

      landingPageName:
        result.landingPageName,

      landingPageType:
        result.landingPageType,

      purchaseOptions:
        resolvedResult.purchaseOptions

    };

  }


  /* ==========================================================
     8. START
     ========================================================== */

  function start(
    profile,
    container,
    config
  ) {

    validateProfile(
      profile
    );


    const target =
      resolveContainer(
        container
      );


    const landingPage =
      buildLandingPage(
        profile
      );


    const ui =
      getProfileUI();


    const result =
      ui.init({

        container:
          target,

        profile:
          profile,

        landingPage:
          landingPage,

        config:
          config || {}

      });


    state.initialized =
      true;

    state.profile =
      profile;

    state.landingPage =
      landingPage;

    state.container =
      target;


    return result;

  }


  /* ==========================================================
     9. LAUNCH
     ========================================================== */

  function launch(
    profile,
    container,
    config
  ) {

    return start(
      profile,
      container,
      config
    );

  }


  /* ==========================================================
     10. UPDATE
     ========================================================== */

  function update(profile) {

    validateProfile(
      profile
    );


    /*
     * If Build #7 has not been initialized yet,
     * update becomes a normal start.
     */

    if (!state.initialized) {

      return start(
        profile,
        state.container
      );

    }


    const landingPage =
      buildLandingPage(
        profile
      );


    const ui =
      getProfileUI();


    if (
      typeof ui.update !==
      'function'
    ) {

      throw new Error(
        'Build #6 update() is unavailable.'
      );

    }


    const result =
      ui.update(
        profile,
        landingPage
      );


    state.profile =
      profile;

    state.landingPage =
      landingPage;


    return result;

  }


  /* ==========================================================
     11. RESTART
     ========================================================== */

  function restart(
    profile,
    container,
    config
  ) {

    destroy();


    return start(
      profile,
      container,
      config
    );

  }


  /* ==========================================================
     12. DESTROY
     ========================================================== */

  function destroy() {

    if (
      window.CoffeeTasteProfileUI &&
      typeof window.CoffeeTasteProfileUI.destroy ===
      'function'
    ) {

      window.CoffeeTasteProfileUI.destroy();

    }


    state.initialized =
      false;

    state.profile =
      null;

    state.landingPage =
      null;

    state.container =
      null;


    return true;

  }


  /* ==========================================================
     13. GET STATE
     ========================================================== */

  function getState() {

    return {

      version:
        VERSION,

      build:
        BUILD,

      initialized:
        state.initialized,

      profile:
        state.profile,

      landingPage:
        state.landingPage,

      container:
        state.container

    };

  }


  /* ==========================================================
     14. VALIDATE
     ========================================================== */

  function validate() {

    const errors = [];


    /*
     * Build #2
     */

    if (!window.CoffeeLandingPages) {

      errors.push(
        'Build #2 CoffeeLandingPages is unavailable.'
      );

    } else if (
      typeof window.CoffeeLandingPages.buildResult !==
      'function'
    ) {

      errors.push(
        'Build #2 buildResult() is unavailable.'
      );

    }


    /*
     * Build #3
     */

    if (!window.CoffeeShopifyProducts) {

      errors.push(
        'Build #3 CoffeeShopifyProducts is unavailable.'
      );

    } else if (
      typeof window.CoffeeShopifyProducts.resolveLandingPage !==
      'function'
    ) {

      errors.push(
        'Build #3 resolveLandingPage() is unavailable.'
      );

    }


    /*
     * Build #6
     */

    if (!window.CoffeeTasteProfileUI) {

      errors.push(
        'Build #6 CoffeeTasteProfileUI is unavailable.'
      );

    } else if (
      typeof window.CoffeeTasteProfileUI.init !==
      'function'
    ) {

      errors.push(
        'Build #6 init() is unavailable.'
      );

    }


    return {

      valid:
        errors.length === 0,

      version:
        VERSION,

      build:
        BUILD,

      dependencies: {

        build2:
          !!window.CoffeeLandingPages,

        build3:
          !!window.CoffeeShopifyProducts,

        build6:
          !!window.CoffeeTasteProfileUI

      },

      errors:
        errors

    };

  }


  /* ==========================================================
     15. PUBLIC API
     ========================================================== */

  const API = {

    version:
      VERSION,

    build:
      BUILD,

    start,

    launch,

    update,

    restart,

    destroy,

    getState,

    validate

  };


  /* ==========================================================
     16. GLOBAL EXPORT
     ========================================================== */

  window.CoffeeTasteProfileOrchestrator =
    API;


})(window);